import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rapportAPI, eiendomAPI } from '../services/api';
import { forklarReferanse } from '../utils/tek-referanser';
import './Rapport.css';

function Rapport() {
  const { id } = useParams(); // eiendom_id
  const navigate = useNavigate();
  const [rapport, setRapport] = useState(null);
  const [eiendom, setEiendom] = useState(null);
  const [validering, setValidering] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      // Hent validering først
      const valideringRes = await rapportAPI.valider(id);
      setValidering(valideringRes.data);

      // Hent eiendomsdata
      const eiendomRes = await eiendomAPI.getById(id);
      setEiendom(eiendomRes.data);

      // Hent rapport-data
      const rapportRes = await rapportAPI.hent(id);
      setRapport(rapportRes.data);
    } catch (err) {
      setError('Kunne ikke hente rapport: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      // Vis loading-melding
      const originalButtonText = document.querySelector('.btn-primary').textContent;
      document.querySelector('.btn-primary').textContent = '⏳ Genererer PDF...';
      document.querySelector('.btn-primary').disabled = true;

      // Hent HTML-innholdet fra rapporten
      const reportElement = document.querySelector('.rapport-content');
      if (!reportElement) {
        throw new Error('Rapport-innhold ikke funnet');
      }

      // Generer full HTML med inline CSS
      const reportHTML = `
<!DOCTYPE html>
<html lang="no">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Tilstandsrapport - ${eiendom.adresse}</title>
  <style>
    ${await fetch('/Rapport.css').then(r => r.text()).catch(() => '')}

    /* PDF-spesifikk styling */
    body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
    .forelopig-vannmerke { display: ${erForelopig ? 'block' : 'none'}; }
    .no-print { display: none !important; }

    /* Bedre sideskift */
    .rapport-seksjon { page-break-inside: avoid; }
    .bygning-seksjon { page-break-before: always; }
    .bygningsdel-rapport { page-break-inside: avoid; margin-bottom: 30px; }

    /* Bilder i PDF */
    img { max-width: 100%; height: auto; page-break-inside: avoid; }
    .bilder-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
  </style>
</head>
<body>
  ${reportElement.outerHTML}
</body>
</html>
      `;

      // Send til backend for PDF-generering
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/rapport/${id}/pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ html: reportHTML })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Last ned PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tilstandsrapport-${eiendom.adresse?.replace(/[^a-zA-Z0-9]/g, '-') || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Tilbakestill knapp
      document.querySelector('.btn-primary').textContent = originalButtonText;
      document.querySelector('.btn-primary').disabled = false;

    } catch (error) {
      console.error('PDF-generering feil:', error);

      // Tilbakestill knapp først
      const btn = document.querySelector('.btn-primary');
      if (btn) {
        btn.textContent = '📥 Last ned PDF';
        btn.disabled = false;
      }

      // Hvis Browser Rendering ikke er tilgjengelig, bruk print i stedet
      if (error.message?.includes('Browser Rendering') || error.message?.includes('ikke tilgjengelig')) {
        const usePrint = window.confirm(
          'PDF-generering er ikke aktivert ennå.\n\n' +
          'Vil du bruke nettleserens print-funksjon i stedet?\n\n' +
          'I print-dialogen kan du velge "Lagre som PDF".'
        );
        if (usePrint) {
          handlePrint();
        }
      } else {
        alert(`Kunne ikke generere PDF:\n\n${error.message}\n\nPrøv å bruke "Skriv ut" funksjonen i stedet.`);
      }
    }
  };

  const bygningsdelTypeNavn = (type) => {
    const typer = {
      yttertak: 'Yttertak',
      yttervegger: 'Yttervegger',
      vinduer_dorer: 'Vinduer og dører',
      fundament_sokkel: 'Fundament og sokkel',
      drenering: 'Drenering',
      elektrisk: 'Elektrisk anlegg',
      vvs: 'VVS-anlegg',
      ventilasjon: 'Ventilasjon',
      brannsikkerhet: 'Brannsikkerhet',
    };
    return typer[type] || type;
  };

  const romtypeNavn = (type) => {
    const typer = {
      bad: 'Bad',
      wc: 'WC',
      vaskerom: 'Vaskerom',
      kjokken: 'Kjøkken',
      stue: 'Stue',
      soverom: 'Soverom',
      gang: 'Gang',
      bod: 'Bod',
      annet: 'Annet',
    };
    return typer[type] || type;
  };

  const etasjeNavn = (etasje) => {
    const etasjer = {
      kjeller: 'Kjeller',
      etasje_1: '1. etasje',
      etasje_2: '2. etasje',
      etasje_3: '3. etasje',
      loft: 'Loft',
    };
    return etasjer[etasje] || etasje;
  };

  if (loading) return <div className="loading">Laster rapport...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!rapport || !eiendom) return <div className="error-message">Rapport ikke funnet</div>;

  const erForelopig = !validering?.erKlar;

  // Samle alle TG3, TG2 og TGIU funn fra bygningsdeler
  const tg3Funn = [];
  const tg2Funn = [];
  const tgiuFunn = [];

  rapport.bygninger?.forEach((bygning, bygningIdx) => {
    bygning.bygningsdeler?.forEach((del, delIdx) => {
      del.svar?.forEach((svar) => {
        const anchorId = `bygning-${bygningIdx}-del-${delIdx}`;
        const funnData = {
          bygning: bygning.bygning_navn || bygning.bygning_type,
          bygningsdel: bygningsdelTypeNavn(del.bygningsdel_type),
          beskrivelse: svar.kommentar || svar.svar_verdi,
          anchorId
        };

        if (svar.svar_verdi?.includes('TG3')) {
          tg3Funn.push(funnData);
        } else if (svar.svar_verdi?.includes('TG2')) {
          tg2Funn.push(funnData);
        } else if (svar.svar_verdi?.includes('TGIU')) {
          tgiuFunn.push(funnData);
        }
      });
    });
  });

  return (
    <div className="rapport-page">
      {/* Navigation (skjules ved print) */}
      <nav className="navbar no-print">
        <div className="navbar-content">
          <button onClick={() => navigate(`/eiendom/${id}`)} className="btn-back">
            ← Tilbake til eiendom
          </button>
          <h1>Tilstandsrapport</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleDownloadPDF} className="btn-primary">
              📥 Last ned PDF
            </button>
            <button onClick={handlePrint} className="btn-secondary">
              🖨️ Skriv ut
            </button>
          </div>
        </div>
      </nav>

      {/* FORELØPIG RAPPORT vannmerke */}
      {erForelopig && (
        <div className="forelopig-vannmerke">
          FORELØPIG RAPPORT
        </div>
      )}

      {/* Rapport innhold */}
      <div className="rapport-content">
        {/* Advarsel ved foreløpig rapport (kun på skjerm) */}
        {erForelopig && (
          <div className="advarsel-banner no-print">
            <h3>⚠️ Dette er en foreløpig rapport</h3>
            <p>
              Befaringen er ikke fullført. Kun {validering.fullforteBygningsdeler} av {validering.totaltAntallBygningsdeler} bygningsdeler er inspisert ({validering.prosent}%).
            </p>
            <p>
              <strong>Denne rapporten kan ikke brukes som endelig tilstandsrapport.</strong>
            </p>
          </div>
        )}

        {/* Rapport header */}
        <div className="rapport-header">
          <h1>Tilstandsrapport for eiendom</h1>
          {erForelopig && (
            <div className="forelopig-merke">
              <strong>FORELØPIG RAPPORT</strong> - Ikke fullstendig befaring
            </div>
          )}
          <div className="rapport-metadata">
            <p><strong>Dato:</strong> {new Date().toLocaleDateString('nb-NO')}</p>
            <p><strong>Takstmann:</strong> {rapport.takstmann?.fornavn} {rapport.takstmann?.etternavn}</p>
            {rapport.takstmann?.sertifikatnummer && (
              <p><strong>Sertifikat:</strong> {rapport.takstmann.sertifikatnummer}</p>
            )}
          </div>
        </div>

        {/* Oppsummering av funn (TG3, TG2, TGIU) */}
        {(tg3Funn.length > 0 || tg2Funn.length > 0 || tgiuFunn.length > 0) && (
          <section className="rapport-seksjon" style={{marginTop: '30px', marginBottom: '30px', pageBreakInside: 'avoid'}}>
            <h2>📋 Oppsummering av funn</h2>
            <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>
              Oversikt over bygningsdeler med avvik. Klikk for å gå til detaljert beskrivelse.
            </p>

            {/* TG3 - Kritiske avvik */}
            {tg3Funn.length > 0 && (
              <div style={{marginBottom: '25px'}}>
                <h3 style={{color: '#d32f2f', fontSize: '18px', marginBottom: '12px'}}>
                  🔴 TG3 - Vesentlige avvik ({tg3Funn.length})
                </h3>
                <div style={{backgroundColor: '#ffebee', border: '2px solid #f44336', borderRadius: '8px', padding: '15px'}}>
                  {tg3Funn.map((funn, idx) => (
                    <div key={idx} style={{
                      marginBottom: idx < tg3Funn.length - 1 ? '15px' : '0',
                      paddingBottom: idx < tg3Funn.length - 1 ? '15px' : '0',
                      borderBottom: idx < tg3Funn.length - 1 ? '1px solid #ffcdd2' : 'none'
                    }}>
                      <div style={{fontWeight: 'bold', fontSize: '15px', marginBottom: '5px'}}>
                        {funn.bygning} - {funn.bygningsdel}
                      </div>
                      <div style={{fontSize: '14px', color: '#424242', marginBottom: '8px'}}>
                        {funn.beskrivelse?.substring(0, 150)}{funn.beskrivelse?.length > 150 ? '...' : ''}
                      </div>
                      <a
                        href={`#${funn.anchorId}`}
                        style={{
                          fontSize: '13px',
                          color: '#1976d2',
                          textDecoration: 'none',
                          fontWeight: '500'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(funn.anchorId)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        → Gå til detaljert beskrivelse
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TG2 - Moderate avvik */}
            {tg2Funn.length > 0 && (
              <div style={{marginBottom: '25px'}}>
                <h3 style={{color: '#f57c00', fontSize: '18px', marginBottom: '12px'}}>
                  🟠 TG2 - Moderate avvik ({tg2Funn.length})
                </h3>
                <div style={{backgroundColor: '#fff3e0', border: '2px solid #ff9800', borderRadius: '8px', padding: '15px'}}>
                  {tg2Funn.map((funn, idx) => (
                    <div key={idx} style={{
                      marginBottom: idx < tg2Funn.length - 1 ? '15px' : '0',
                      paddingBottom: idx < tg2Funn.length - 1 ? '15px' : '0',
                      borderBottom: idx < tg2Funn.length - 1 ? '1px solid #ffe0b2' : 'none'
                    }}>
                      <div style={{fontWeight: 'bold', fontSize: '15px', marginBottom: '5px'}}>
                        {funn.bygning} - {funn.bygningsdel}
                      </div>
                      <div style={{fontSize: '14px', color: '#424242', marginBottom: '8px'}}>
                        {funn.beskrivelse?.substring(0, 150)}{funn.beskrivelse?.length > 150 ? '...' : ''}
                      </div>
                      <a
                        href={`#${funn.anchorId}`}
                        style={{
                          fontSize: '13px',
                          color: '#1976d2',
                          textDecoration: 'none',
                          fontWeight: '500'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(funn.anchorId)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        → Gå til detaljert beskrivelse
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TGIU - Tilleggsundersøkelse */}
            {tgiuFunn.length > 0 && (
              <div style={{marginBottom: '0'}}>
                <h3 style={{color: '#0288d1', fontSize: '18px', marginBottom: '12px'}}>
                  🔵 TGIU - Tilleggsundersøkelse ({tgiuFunn.length})
                </h3>
                <div style={{backgroundColor: '#e1f5fe', border: '2px solid #03a9f4', borderRadius: '8px', padding: '15px'}}>
                  {tgiuFunn.map((funn, idx) => (
                    <div key={idx} style={{
                      marginBottom: idx < tgiuFunn.length - 1 ? '15px' : '0',
                      paddingBottom: idx < tgiuFunn.length - 1 ? '15px' : '0',
                      borderBottom: idx < tgiuFunn.length - 1 ? '1px solid #b3e5fc' : 'none'
                    }}>
                      <div style={{fontWeight: 'bold', fontSize: '15px', marginBottom: '5px'}}>
                        {funn.bygning} - {funn.bygningsdel}
                      </div>
                      <div style={{fontSize: '14px', color: '#424242', marginBottom: '8px'}}>
                        {funn.beskrivelse?.substring(0, 150)}{funn.beskrivelse?.length > 150 ? '...' : ''}
                      </div>
                      <a
                        href={`#${funn.anchorId}`}
                        style={{
                          fontSize: '13px',
                          color: '#1976d2',
                          textDecoration: 'none',
                          fontWeight: '500'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(funn.anchorId)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        → Gå til detaljert beskrivelse
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Eiendomsopplysninger */}
        <section className="rapport-seksjon">
          <h2>Eiendomsopplysninger</h2>
          <table className="info-tabell">
            <tbody>
              <tr>
                <td><strong>Adresse:</strong></td>
                <td>{eiendom.adresse}</td>
              </tr>
              <tr>
                <td><strong>Postnummer:</strong></td>
                <td>{eiendom.postnr} {eiendom.poststed}</td>
              </tr>
              <tr>
                <td><strong>Kommune:</strong></td>
                <td>{eiendom.kommune}</td>
              </tr>
              {eiendom.gnr && eiendom.bnr && (
                <tr>
                  <td><strong>Gårdsnr/Bruksnr:</strong></td>
                  <td>Gnr {eiendom.gnr}, bnr {eiendom.bnr}</td>
                </tr>
              )}
              {eiendom.eier_navn && (
                <tr>
                  <td><strong>Eier:</strong></td>
                  <td>{eiendom.eier_navn}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* Eiendomsbilder */}
        {rapport.eiendomBilder && rapport.eiendomBilder.length > 0 && (
          <section className="rapport-seksjon bilder-seksjon">
            <h2>📸 Bilder av eiendommen</h2>
            <p style={{color: '#666', fontSize: '14px', marginBottom: '15px'}}>
              Oversiktsbilder av tomt, adkomst, parkering og omgivelser
            </p>
            <div className="bilder-grid">
              {rapport.eiendomBilder.map((bilde) => (
                <div key={bilde.id} className="bilde-item-rapport">
                  <img src={bilde.cloudflare_r2_url} alt={bilde.filnavn} />
                  {bilde.bildetekst && (
                    <p className="bildetekst-rapport">{bilde.bildetekst}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Bygninger */}
        {rapport.bygninger && rapport.bygninger.map((bygning, bygningIdx) => (
          <section key={bygning.id} className="rapport-seksjon bygning-seksjon">
            <h2>
              {bygningIdx + 1}. {bygning.bygning_navn || bygning.bygning_type}
              {bygning.byggeaar && ` (${bygning.byggeaar})`}
            </h2>

            {/* Arealoppgaver i henhold til §2-20 NS3600:2025 */}
            {(bygning.bra || bygning.bra_i || bygning.bra_e || bygning.bra_b || bygning.tba || bygning.gua || bygning.alh) && (
              <div style={{marginTop: '15px', marginBottom: '15px'}}>
                <h4 style={{fontSize: '14px', marginBottom: '8px'}}>Arealoppgaver (§2-20 NS3600:2025 / NS 3940:2023)</h4>
                {bygning.bra && (
                  <p style={{margin: '4px 0'}}><strong>BRA (Bruksareal totalt):</strong> {bygning.bra} m²</p>
                )}
                {bygning.bra_i && (
                  <p style={{margin: '4px 0'}}><strong>BRA-i (Internt bruksareal):</strong> {bygning.bra_i} m²</p>
                )}
                {bygning.bra_e && (
                  <p style={{margin: '4px 0', color: '#666'}}><strong>BRA-e (Eksternt bruksareal):</strong> {bygning.bra_e} m²</p>
                )}
                {bygning.bra_b && (
                  <p style={{margin: '4px 0', color: '#666'}}><strong>BRA-b (Innglasset balkong/veranda):</strong> {bygning.bra_b} m²</p>
                )}
                {bygning.tba && (
                  <p style={{margin: '4px 0', color: '#666'}}><strong>TBA (Terrasse/balkong åpent):</strong> {bygning.tba} m²</p>
                )}
                {bygning.gua && (
                  <p style={{margin: '4px 0', color: '#666'}}><strong>GUA (Gulvareal totalt):</strong> {bygning.gua} m²</p>
                )}
                {bygning.alh && (
                  <p style={{margin: '4px 0', color: '#666'}}><strong>ALH (Areal lav himlingshøyde):</strong> {bygning.alh} m²</p>
                )}
              </div>
            )}

            {/* Bygningsbilder */}
            {bygning.bilder && bygning.bilder.length > 0 && (
              <div className="bilder-seksjon" style={{marginTop: '20px', marginBottom: '20px'}}>
                <h3>📸 Bilder av {bygning.bygning_navn || bygning.bygning_type}</h3>
                <p style={{color: '#666', fontSize: '14px', marginBottom: '15px'}}>
                  Fasader, tak, grunnmur
                </p>
                <div className="bilder-grid">
                  {bygning.bilder.map((bilde) => (
                    <div key={bilde.id} className="bilde-item-rapport">
                      <img src={bilde.cloudflare_r2_url} alt={bilde.filnavn} />
                      {bilde.bildetekst && (
                        <p className="bildetekst-rapport">{bilde.bildetekst}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bygningsdeler (§2-18) */}
            {bygning.bygningsdeler && bygning.bygningsdeler.length > 0 && (
              <div className="bygningsdeler-seksjon">
                <h3>Bygningsdeler i henhold til §2-18 Forskrift om tryggere bolighandel</h3>

                {bygning.bygningsdeler.map((del, delIdx) => (
                  <div key={del.id} className="bygningsdel-rapport" id={`bygning-${bygningIdx}-del-${delIdx}`}>
                    <h4>
                      {bygningsdelTypeNavn(del.bygningsdel_type)}
                      {bygning.byggeaar && (
                        <span style={{fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px'}}>
                          ({forklarReferanse(bygning.byggeaar, del.renoverings_aar)})
                        </span>
                      )}
                    </h4>

                    {/* Type-spesifikasjon */}
                    {del.type_kommentar && (
                      <div style={{marginBottom: '10px', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', borderLeft: '3px solid #4a90e2'}}>
                        <strong>📝 Type/Detaljer:</strong> {del.type_kommentar}
                      </div>
                    )}

                    <div className="status-info">
                      <strong>Status:</strong>{' '}
                      <span className={`status-badge status-${del.status}`}>
                        {del.status === 'fullfort' ? '✓ Fullført' :
                         del.status === 'under_arbeid' ? '⏳ Under arbeid' :
                         'Ikke påbegynt'}
                      </span>
                    </div>

                    {/* Spørsmål og svar */}
                    {del.svar && del.svar.length > 0 && (
                      <div className="svar-liste">
                        {del.svar.map((svar, svarIdx) => {
                          // Finn bilder knyttet til dette spesifikke spørsmålet
                          const sporsmalBilder = del.bilder ? del.bilder.filter(b => b.sporsmal_id === svar.sporsmal_id) : [];

                          return (
                            <div key={svar.id} className="svar-item">
                              <div className="sporsmal">
                                <strong>Q{svarIdx + 1}:</strong> {svar.sporsmal_tekst}
                                {svar.ns3600_referanse && (
                                  <span className="ns3600-ref"> ({svar.ns3600_referanse})</span>
                                )}
                              </div>
                              <div className="svar">
                                <strong>Svar:</strong> {svar.svar_verdi || '(Ikke besvart)'}
                              </div>

                              {/* Spørsmål-spesifikke bilder */}
                              {sporsmalBilder.length > 0 && (
                                <div className="sporsmal-bilder" style={{marginTop: '10px', marginLeft: '20px'}}>
                                  <p style={{fontSize: '13px', color: '#666', marginBottom: '8px'}}>
                                    📸 Dokumentasjonsbilder ({sporsmalBilder.length})
                                  </p>
                                  <div className="bilder-grid" style={{gap: '10px'}}>
                                    {sporsmalBilder.map((bilde) => (
                                      <div key={bilde.id} className="bilde-item-rapport">
                                        <img src={bilde.cloudflare_r2_url} alt={bilde.filnavn} style={{maxWidth: '200px'}} />
                                        {bilde.bildetekst && (
                                          <p className="bildetekst-rapport" style={{fontSize: '12px'}}>{bilde.bildetekst}</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Bilder */}
                    {del.bilder && del.bilder.length > 0 && (
                      <div className="bilder-seksjon">
                        <h5>Dokumentasjonsbilder ({del.bilder.length})</h5>
                        <div className="bilder-grid">
                          {del.bilder.map((bilde) => (
                            <div key={bilde.id} className="bilde-item">
                              <img
                                src={bilde.cloudflare_r2_url}
                                alt={bilde.filnavn}
                                className="rapport-bilde"
                              />
                              {bilde.beskrivelse && (
                                <p className="bilde-beskrivelse">{bilde.beskrivelse}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Videre undersøkelser */}
                    {del.videre_undersokelser && del.videre_undersokelser.length > 0 && (
                      <div className="videre-undersokelser-seksjon" style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#fff3e0',
                        borderLeft: '4px solid #ff9800',
                        borderRadius: '4px'
                      }}>
                        <h5 style={{margin: '0 0 12px 0', color: '#e65100'}}>
                          🔍 Anbefalte videre undersøkelser
                        </h5>
                        {del.videre_undersokelser.map((undersokelse, idx) => (
                          <div key={undersokelse.id} style={{
                            marginBottom: '15px',
                            paddingBottom: '15px',
                            borderBottom: idx < del.videre_undersokelser.length - 1 ? '1px solid #ffcc80' : 'none'
                          }}>
                            <div style={{marginBottom: '8px'}}>
                              <span style={{
                                display: 'inline-block',
                                padding: '3px 8px',
                                backgroundColor: undersokelse.prioritet === 'kritisk' ? '#f44336' :
                                                 undersokelse.prioritet === 'anbefalt' ? '#ff9800' : '#4caf50',
                                color: 'white',
                                borderRadius: '3px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                marginRight: '8px'
                              }}>
                                {undersokelse.prioritet === 'kritisk' ? '🔴 KRITISK' :
                                 undersokelse.prioritet === 'anbefalt' ? '🟡 ANBEFALT' : '🟢 VALGFRI'}
                              </span>
                              <strong style={{fontSize: '14px'}}>
                                {undersokelse.undersokelse_kategori.charAt(0).toUpperCase() + undersokelse.undersokelse_kategori.slice(1).replace('_', ' ')}
                              </strong>
                            </div>

                            <div style={{marginLeft: '10px', fontSize: '13px', lineHeight: '1.6'}}>
                              <p style={{margin: '5px 0'}}>
                                <strong>Årsak:</strong> {undersokelse.arsak}
                              </p>
                              <p style={{margin: '5px 0'}}>
                                <strong>Omfang:</strong> {undersokelse.omfang}
                              </p>
                              {undersokelse.metode && (
                                <p style={{margin: '5px 0'}}>
                                  <strong>Metode:</strong> {undersokelse.metode}
                                </p>
                              )}
                              {(undersokelse.kostnad_min || undersokelse.kostnad_max) && (
                                <p style={{margin: '5px 0'}}>
                                  <strong>Estimert kostnad:</strong> {' '}
                                  {undersokelse.kostnad_min?.toLocaleString('no-NO')} - {undersokelse.kostnad_max?.toLocaleString('no-NO')} kr
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {del.status !== 'fullfort' && (
                      <div className="ufullstendig-advarsel">
                        ⚠️ Denne bygningsdelen er ikke fullstendig inspisert
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Rom */}
            {bygning.rom && bygning.rom.length > 0 && (
              <div className="rom-seksjon">
                <h3>Rominspisering</h3>

                {bygning.rom.map((rom) => (
                  <div key={rom.id} className="rom-rapport">
                    <h4>
                      {romtypeNavn(rom.romtype)} {rom.romnummer} - {etasjeNavn(rom.etasje)}
                    </h4>

                    {rom.tilstandsgrad && (
                      <p><strong>Tilstandsgrad:</strong> {rom.tilstandsgrad}</p>
                    )}

                    {/* Rom-spørsmål og svar */}
                    {rom.svar && rom.svar.length > 0 && (
                      <div className="svar-liste">
                        {rom.svar.map((svar, svarIdx) => (
                          <div key={svar.id} className="svar-item">
                            <div className="sporsmal">
                              <strong>Q{svarIdx + 1}:</strong> {svar.sporsmal_tekst}
                            </div>
                            <div className="svar">
                              <strong>Svar:</strong> {svar.svar_verdi || '(Ikke besvart)'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Rom-bilder */}
                    {rom.bilder && rom.bilder.length > 0 && (
                      <div className="bilder-seksjon">
                        <h5>Bilder ({rom.bilder.length})</h5>
                        <div className="bilder-grid">
                          {rom.bilder.map((bilde) => (
                            <div key={bilde.id} className="bilde-item">
                              <img
                                src={bilde.cloudflare_r2_url}
                                alt={bilde.filnavn}
                                className="rapport-bilde"
                              />
                              {bilde.beskrivelse && (
                                <p className="bilde-beskrivelse">{bilde.beskrivelse}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}

        {/* SAMLET OPPSUMMERING: Videre undersøkelser */}
        {(() => {
          // Samle alle videre undersøkelser fra alle bygningsdeler
          const alleUndersokelser = [];
          rapport.bygninger?.forEach((bygning) => {
            bygning.bygningsdeler?.forEach((del) => {
              if (del.videre_undersokelser && del.videre_undersokelser.length > 0) {
                del.videre_undersokelser.forEach((u) => {
                  alleUndersokelser.push({
                    ...u,
                    bygningsdel_navn: del.bygningsdel_type,
                    bygningsdel_id: del.id
                  });
                });
              }
            });
          });

          if (alleUndersokelser.length === 0) return null;

          // Grupper etter prioritet
          const kritiske = alleUndersokelser.filter(u => u.prioritet === 'kritisk');
          const anbefalte = alleUndersokelser.filter(u => u.prioritet === 'anbefalt');
          const valgfrie = alleUndersokelser.filter(u => u.prioritet === 'valgfri');

          // Beregn totalkostnader
          const beregnTotalKostnad = (liste) => {
            let minTotal = 0;
            let maxTotal = 0;
            liste.forEach(u => {
              minTotal += u.kostnad_min || 0;
              maxTotal += u.kostnad_max || 0;
            });
            return { min: minTotal, max: maxTotal };
          };

          const kritiskKostnad = beregnTotalKostnad(kritiske);
          const anbefaltKostnad = beregnTotalKostnad(anbefalte);
          const valgfriKostnad = beregnTotalKostnad(valgfrie);
          const totalKostnad = {
            min: kritiskKostnad.min + anbefaltKostnad.min,
            max: kritiskKostnad.max + anbefaltKostnad.max
          };

          return (
            <section className="samlet-oppsummering-videre-undersokelser" style={{
              marginTop: '40px',
              marginBottom: '40px',
              padding: '25px',
              backgroundColor: '#fff9e6',
              border: '2px solid #ff9800',
              borderRadius: '8px'
            }}>
              <h2 style={{
                margin: '0 0 20px 0',
                color: '#e65100',
                fontSize: '24px',
                borderBottom: '2px solid #ff9800',
                paddingBottom: '10px'
              }}>
                10. OPPSUMMERING - ANBEFALTE VIDERE UNDERSØKELSER
              </h2>

              <p style={{
                marginBottom: '20px',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#333'
              }}>
                Basert på gjennomført tilstandsanalyse anbefales følgende videre undersøkelser for å verifisere bygningsdeler som ikke kunne inspiseres fullt ut, eller hvor det er funnet avvik som krever nærmere dokumentasjon.
              </p>

              {/* KRITISKE */}
              {kritiske.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{
                    margin: '0 0 15px 0',
                    color: '#f44336',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🔴 KRITISKE UNDERSØKELSER ({kritiske.length} stk)
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '15px',
                    fontStyle: 'italic'
                  }}>
                    Disse undersøkelsene er påkrevd i henhold til NS3600 eller TEK17, eller gjelder alvorlige sikkerhetsmessige forhold.
                  </p>

                  <ol style={{ margin: 0, paddingLeft: '20px' }}>
                    {kritiske.map((u, idx) => (
                      <li key={u.id} style={{
                        marginBottom: '15px',
                        fontSize: '14px',
                        lineHeight: '1.6'
                      }}>
                        <strong>
                          {u.undersokelse_kategori?.charAt(0).toUpperCase() +
                           u.undersokelse_kategori?.slice(1).replace('_', ' ')}
                        </strong> ({u.bygningsdel_navn})
                        <br />
                        <span style={{ fontSize: '13px', color: '#555' }}>
                          {u.arsak}
                        </span>
                        {(u.kostnad_min || u.kostnad_max) && (
                          <>
                            <br />
                            <span style={{ fontSize: '13px', color: '#f44336', fontWeight: 'bold' }}>
                              Kostnad: {u.kostnad_min?.toLocaleString('no-NO')} - {u.kostnad_max?.toLocaleString('no-NO')} kr
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ol>

                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#ffebee',
                    borderLeft: '4px solid #f44336',
                    borderRadius: '4px'
                  }}>
                    <strong style={{ color: '#c62828' }}>
                      TOTALT KRITISK: {kritiskKostnad.min.toLocaleString('no-NO')} - {kritiskKostnad.max.toLocaleString('no-NO')} kr
                    </strong>
                  </div>
                </div>
              )}

              {/* ANBEFALTE */}
              {anbefalte.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{
                    margin: '0 0 15px 0',
                    color: '#ff9800',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🟡 ANBEFALTE UNDERSØKELSER ({anbefalte.length} stk)
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '15px',
                    fontStyle: 'italic'
                  }}>
                    Disse undersøkelsene anbefales sterkt for å sikre fullstendig dokumentasjon av boligens tilstand.
                  </p>

                  <ol style={{ margin: 0, paddingLeft: '20px' }}>
                    {anbefalte.map((u, idx) => (
                      <li key={u.id} style={{
                        marginBottom: '15px',
                        fontSize: '14px',
                        lineHeight: '1.6'
                      }}>
                        <strong>
                          {u.undersokelse_kategori?.charAt(0).toUpperCase() +
                           u.undersokelse_kategori?.slice(1).replace('_', ' ')}
                        </strong> ({u.bygningsdel_navn})
                        <br />
                        <span style={{ fontSize: '13px', color: '#555' }}>
                          {u.arsak}
                        </span>
                        {(u.kostnad_min || u.kostnad_max) && (
                          <>
                            <br />
                            <span style={{ fontSize: '13px', color: '#ff9800', fontWeight: 'bold' }}>
                              Kostnad: {u.kostnad_min?.toLocaleString('no-NO')} - {u.kostnad_max?.toLocaleString('no-NO')} kr
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ol>

                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#fff3e0',
                    borderLeft: '4px solid #ff9800',
                    borderRadius: '4px'
                  }}>
                    <strong style={{ color: '#e65100' }}>
                      TOTALT ANBEFALT: {anbefaltKostnad.min.toLocaleString('no-NO')} - {anbefaltKostnad.max.toLocaleString('no-NO')} kr
                    </strong>
                  </div>
                </div>
              )}

              {/* VALGFRIE */}
              {valgfrie.length > 0 && (
                <div style={{ marginBottom: '25px' }}>
                  <h3 style={{
                    margin: '0 0 15px 0',
                    color: '#4caf50',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🟢 VALGFRIE UNDERSØKELSER ({valgfrie.length} stk)
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '15px',
                    fontStyle: 'italic'
                  }}>
                    Disse undersøkelsene er valgfrie, men kan gi merverdi for energiøkonomi eller planlagt oppgradering.
                  </p>

                  <ol style={{ margin: 0, paddingLeft: '20px' }}>
                    {valgfrie.map((u, idx) => (
                      <li key={u.id} style={{
                        marginBottom: '15px',
                        fontSize: '14px',
                        lineHeight: '1.6'
                      }}>
                        <strong>
                          {u.undersokelse_kategori?.charAt(0).toUpperCase() +
                           u.undersokelse_kategori?.slice(1).replace('_', ' ')}
                        </strong> ({u.bygningsdel_navn})
                        <br />
                        <span style={{ fontSize: '13px', color: '#555' }}>
                          {u.arsak}
                        </span>
                        {(u.kostnad_min || u.kostnad_max) && (
                          <>
                            <br />
                            <span style={{ fontSize: '13px', color: '#4caf50', fontWeight: 'bold' }}>
                              Kostnad: {u.kostnad_min?.toLocaleString('no-NO')} - {u.kostnad_max?.toLocaleString('no-NO')} kr
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ol>

                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderLeft: '4px solid #4caf50',
                    borderRadius: '4px'
                  }}>
                    <strong style={{ color: '#2e7d32' }}>
                      TOTALT VALGFRI: {valgfriKostnad.min.toLocaleString('no-NO')} - {valgfriKostnad.max.toLocaleString('no-NO')} kr
                    </strong>
                  </div>
                </div>
              )}

              {/* SAMLET TOTAL */}
              <div style={{
                marginTop: '25px',
                padding: '15px',
                backgroundColor: '#fff',
                border: '3px solid #ff9800',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <h4 style={{
                  margin: '0 0 10px 0',
                  fontSize: '16px',
                  color: '#333'
                }}>
                  SAMLET KOSTNAD (kritisk + anbefalt):
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#e65100'
                }}>
                  {totalKostnad.min.toLocaleString('no-NO')} - {totalKostnad.max.toLocaleString('no-NO')} kr
                </p>
                <p style={{
                  margin: '10px 0 0 0',
                  fontSize: '12px',
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  Kostnadsestimat er veiledende og kan variere avhengig av valg av leverandør og omfang.
                </p>
              </div>

              {/* VIKTIG MERKNAD */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#e3f2fd',
                borderLeft: '4px solid #2196f3',
                borderRadius: '4px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#333'
                }}>
                  <strong>ℹ️ VIKTIG:</strong> Anbefalte videre undersøkelser er basert på NS3600:2025 krav til dokumentasjon.
                  Kritiske undersøkelser bør gjennomføres før overtakelse for å sikre trygg bolighandel.
                  Kjøper og selger må avtale hvem som skal gjennomføre og bekoste undersøkelsene.
                </p>
              </div>
            </section>
          );
        })()}

        {/* Footer */}
        <section className="rapport-footer">
          <hr />
          <p className="footer-tekst">
            Denne rapporten er utarbeidet i henhold til NS3600:2018, NS 3940:2023 (arealberegning) og Forskrift om tryggere bolighandel.
          </p>
          {erForelopig && (
            <p className="footer-advarsel">
              <strong>OBS:</strong> Dette er en foreløpig rapport og erstatter ikke en fullstendig tilstandsrapport.
              Befaringen må fullføres før endelig rapport kan utstedes.
            </p>
          )}

          {/* Kilder og opphavsrett */}
          <div style={{
            marginTop: '30px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderTop: '2px solid #dee2e6',
            fontSize: '12px',
            color: '#666'
          }}>
            <p style={{margin: '0 0 10px 0', fontWeight: 'bold', color: '#333'}}>
              KILDER OG REFERANSER
            </p>
            <p style={{margin: '5px 0', lineHeight: '1.6'}}>
              Rapporten er utarbeidet i henhold til:<br />
              • <strong>NS3600:2018</strong> - Visuell tilstandsanalyse av byggverk (© Standard Norge)<br />
              • <strong>TEK17</strong> - Forskrift om tekniske krav til byggverk (Lovdata.no)<br />
              • <strong>Forskrift om tryggere bolighandel</strong> §2-18, §2-20, §2-22 (Lovdata.no)
            </p>
            <p style={{margin: '10px 0 5px 0', fontSize: '11px'}}>
              Systemet er utviklet av Nesodden Advokatkontor<br />
              Implementert med Claude Code (Anthropic)
            </p>
            <p style={{margin: '5px 0', fontSize: '11px'}}>
              For fullstendig forskriftstekst, se:<br />
              • Lovdata.no (gratis): <a href="https://lovdata.no" target="_blank" rel="noopener noreferrer">https://lovdata.no</a><br />
              • Standard Norge (kjøp): <a href="https://www.standard.no" target="_blank" rel="noopener noreferrer">https://www.standard.no</a>
            </p>
          </div>

          <p className="footer-signatur">
            <br />
            _________________________________<br />
            {rapport.takstmann?.fornavn} {rapport.takstmann?.etternavn}<br />
            Takstmann
          </p>
        </section>
      </div>
    </div>
  );
}

export default Rapport;
